import mixpanel, { Dict } from 'mixpanel-browser';

import { CONSTANT_VARIABLES } from 'src/constants/constants-variables';

export const ORDER_FLOW_STARTED_AT_KEY = 'order_flow_started_at';

const MIXPANEL_TOKEN = import.meta.env.VITE_MIXPANEL_TOKEN as string | undefined;
const ENVIRONMENT = import.meta.env.VITE_ENVIRONMENT as string | undefined;
const IS_PRODUCTION = ENVIRONMENT === CONSTANT_VARIABLES.PRODUCTION;

const PERFORMANCE_CATEGORY_PREFIX = 'performance';

const COOKIE_SCRIPT_EVENTS = [
  'CookieScriptAcceptAll',
  'CookieScriptAccept',
  'CookieScriptReject',
] as const;

type CookieScriptState = { categories?: unknown };

type CookieScriptInstance = {
  currentState?: () => CookieScriptState | undefined;
};

declare global {
  interface Window {
    CookieScript?: { instance?: CookieScriptInstance };
  }
}

const isAdminRoute = () => {
  if (typeof window === 'undefined') return false;
  const { pathname } = window.location;
  return pathname === '/admin' || pathname.startsWith('/admin/');
};

const hasPerformanceConsent = (): boolean => {
  if (typeof window === 'undefined') return false;
  const state = window.CookieScript?.instance?.currentState?.();
  const categories = state?.categories;
  if (!Array.isArray(categories)) return false;
  return categories.some(
    (c) => typeof c === 'string' && c.toLowerCase().startsWith(PERFORMANCE_CATEGORY_PREFIX),
  );
};

const syncConsentFromCookieScript = () => {
  if (!MIXPANEL_TOKEN) return;
  if (hasPerformanceConsent()) {
    mixpanel.opt_in_tracking();
  } else {
    mixpanel.opt_out_tracking();
  }
};

if (MIXPANEL_TOKEN) {
  mixpanel.init(MIXPANEL_TOKEN, {
    api_host: 'https://api-eu.mixpanel.com',
    persistence: 'localStorage',
    track_pageview: 'url-with-path',
    autocapture: false,
    record_sessions_percent: 0,
    debug: !IS_PRODUCTION,
    opt_out_tracking_by_default: true,
  });
  mixpanel.register({
    environment: ENVIRONMENT,
    user_role: 'anonymous',
  });

  if (typeof window !== 'undefined') {
    syncConsentFromCookieScript();
    COOKIE_SCRIPT_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, syncConsentFromCookieScript);
    });
  }
}

const analytics = {
  identify: (userId: string, traits?: Dict) => {
    if (!MIXPANEL_TOKEN) return;
    if (isAdminRoute()) return;
    if (mixpanel.has_opted_out_tracking()) return;
    mixpanel.identify(userId);
    if (traits) mixpanel.people.set(traits);
  },

  track: (event: string, properties?: Dict) => {
    if (!MIXPANEL_TOKEN) return;
    if (isAdminRoute()) return;
    if (mixpanel.has_opted_out_tracking()) return;
    mixpanel.track(event, properties);
  },

  reset: () => {
    sessionStorage.removeItem(ORDER_FLOW_STARTED_AT_KEY);
    if (!MIXPANEL_TOKEN) return;
    mixpanel.reset();
  },

  register: (properties: Dict) => {
    if (!MIXPANEL_TOKEN) return;
    mixpanel.register(properties);
  },

  optIn: () => {
    if (!MIXPANEL_TOKEN) return;
    mixpanel.opt_in_tracking();
  },

  optOut: () => {
    if (!MIXPANEL_TOKEN) return;
    mixpanel.opt_out_tracking();
  },
};

export default analytics;
